import React, { createRef, ReactNode, useEffect, useState } from "react";
import Slider from "react-slick";
import NewsletterModal from "components/NewsletterModal";
import { Button } from "@pepdirect/ui/button";
import cx from "classnames";
import st from "./index.module.scss";
import SliderText from "./SliderText";
import { useRouter } from "next/router";
import FlavorSlide from "components/FlavorSlide";
import { FocusSlide } from "components/FocusSlide";
import YoutubeSustain from "components/YoutubeSustain";

export default function HomePageHeroSlider(): JSX.Element {
  const textSliderRef = createRef<Slider>();
  const buttonSliderRef = createRef<Slider>();
  const mainSliderRef = createRef<Slider>();

  const [currentIndex, setCurrentIndex] = useState<number>(1);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [navState, setNavState] = useState<{
    textNav: Slider | undefined;
    buttonNav: Slider | undefined;
    mainNav: Slider | undefined;
  }>({
    textNav: undefined,
    buttonNav: undefined,
    mainNav: undefined,
  });

  const router = useRouter();

  useEffect(() => {
    mainSliderRef.current?.slickPause();
    setNavState({
      textNav: textSliderRef.current || undefined,
      buttonNav: buttonSliderRef.current || undefined,
      mainNav: mainSliderRef.current || undefined,
    });
    setTimeout(
      (slider) => {
        setCurrentIndex(0);
        slider?.slickPlay();
      },
      500,
      mainSliderRef.current
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appendDots = (dots: ReactNode[]) => <div>{dots}</div>;

  const customPaging = (i: number) => {
    const textClass = cx(
      st.dark,
      st.text,
      i === currentIndex ? st.activeText : ""
    );

    const renderPaging = () => {
      switch (i) {
        case 0:
          return <p className={textClass}>{"Sustains Mind-Body Energy*"}</p>;
        case 1:
          return (
            <p className={textClass}>
              {"Energy & Mental Boost* with Lion’s Mane"}
            </p>
          );
        case 2:
          return <p className={textClass}>{"Hydration // Energy Boost"}</p>;
      }
    };

    return (
      <div className={st.paging}>
        {i === currentIndex ? (
          <div className={cx(st.active, st.dark)} />
        ) : (
          <div className={st.inActive} />
        )}
        <div className={cx(st.textWrapper, st.text)}>{renderPaging()}</div>
      </div>
    );
  };

  const mainSliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    speed: 2000,
    fade: true,
    autoplaySpeed: 4000,
    arrows: false,
    centerMode: true,
    centerPadding: "0",
    swipeToSlide: false,
    appendDots: appendDots,
    customPaging: customPaging,
    beforeChange: (_: number, next: number) => setCurrentIndex(next),
  };

  const textSliderSettings = {
    infinite: true,
    dots: false,
    arrows: false,
    adaptiveHeight: true,
  };

  const buttonSliderSettings = {
    dots: false,
    fade: true,
    arrows: false,
  };

  const [newsletterModalOpen, setNewsletterModalOpen] =
    useState<boolean>(false);

  return (
    <>
      <div className={st.container}>
        <div className={st.textButtonSlider}>
          <div className={st.textButtonWrapper}>
            <Slider
              ref={textSliderRef}
              asNavFor={navState.mainNav}
              {...textSliderSettings}
              className={st.textSlider}
            >
              <SliderText
                smallHeader={<>SUSTAINS MIND-BODY ENERGY*</>}
                largeHeader={<>You can own any moment</>}
                disclaimer={
                  <>
                    *at least 160mg of caffeine provides feelings of increased
                    alertness and reduced tiredness for up to 5 hours
                  </>
                }
                color="rgb(44, 41, 37)"
              />
              <SliderText
                smallHeader={<>ENERGY & MENTAL BOOST* WITH LION’S MANE</>}
                largeHeader={<>You can focus</>}
                disclaimer={
                  <>
                    *at least 75mg of caffeine has been shown to help improve
                    attention
                  </>
                }
                color="rgb(44, 41, 37)"
              />
              <SliderText
                smallHeader={<>Hydration // Energy Boost</>}
                largeHeader={<>You can recover</>}
                disclaimer={<>160mg caffeine // 16 fl oz</>}
                color="rgb(44, 41, 37)"
              />
            </Slider>
            <Slider
              ref={buttonSliderRef}
              asNavFor={navState.textNav}
              {...buttonSliderSettings}
            >
              <Button
                onClick={() =>
                  router.push({
                    pathname: "/shop-all",
                    hash: "products",
                    query: {
                      canSize: "All",
                      nutrition: "All",
                      subline: "All",
                      unAvailableProducts: "show",
                    },
                  })
                }
                className={cx(st.button, st.dark)}
              >
                Shop All
              </Button>
              <Button
                onClick={() =>
                  router.push({
                    pathname: "/shop-all",
                    hash: "products",
                    query: {
                      canSize: "All",
                      nutrition: "All",
                      subline: "Focus",
                      unAvailableProducts: "show",
                    },
                  })
                }
                className={cx(st.button, st.dark)}
              >
                Buy Now
              </Button>
              <Button
                onClick={() =>
                  router.push({
                    pathname: "/shop-all",
                    hash: "products",
                    query: {
                      canSize: "All",
                      nutrition: "All",
                      subline: "Recovery",
                      unAvailableProducts: "show",
                    },
                  })
                }
                className={cx(st.button, st.dark)}
              >
                Buy Now
              </Button>
            </Slider>
          </div>
        </div>
        <Slider
          ref={mainSliderRef}
          asNavFor={navState.buttonNav}
          className={st["slick-slider-custom"]}
          {...mainSliderSettings}
        >
          <YoutubeSustain
            isVideoModalOpen={isVideoModalOpen}
            openVideoModal={() => {
              setIsVideoModalOpen(true);
            }}
            closeVideoModal={() => {
              setIsVideoModalOpen(false);
            }}
          />
          <FocusSlide />
          <FlavorSlide
            bgImg="https://www.datocms-assets.com/74813/1709075345-rs-2024-sustainedenergy-web_desktop-2.png"
            mobileBgImg="https://www.datocms-assets.com/74813/1709074181-rs-2024-sustainedenergy-web_mobile-2.png"
            canImg="https://www.datocms-assets.com/74813/1711670903-can_desktop_1026x1350-4-1.png"
          />
        </Slider>
      </div>
      <NewsletterModal
        isOpen={newsletterModalOpen}
        close={() => setNewsletterModalOpen(false)}
      />
    </>
  );
}
